import { readFileSync } from "fs";
import crypto from "node:crypto";
import { join } from "path";
import { HONEYBADGER_IGNORED_STATUS_CODES, INITIAL_VECTOR, SALT, SECRET_KEY, HONEYBADGER_IGNORED_ERROR_MESSAGES, } from "@constants/common";
import { ERRORS } from "@constants/texts/errors";
import Honeybadger from "@honeybadger-io/js";
import consoleLogFormatted from "consoleLogFormatted";
import { camelToSnakeCase } from "neetocist";
export const createShardObject = ({ currentShard = 0, status = "running", duration = null, }) => ({
    [currentShard]: { status, duration },
});
export const convertBufferToBlob = (buffer, contentType) => new Blob([Uint8Array.from(buffer)], { type: contentType });
export const waitUntilTimeout = (timeout) => new Promise(resolve => setTimeout(resolve, timeout));
export const waitUntilCondition = async (condition, timeout = 6.5 * 60 * 1000 // Default timeout after 6.5 minutes
) => {
    const startTime = new Date();
    const timeoutTime = new Date(startTime.getTime() + timeout);
    while (!condition()) {
        if (new Date().getTime() >= timeoutTime.getTime()) {
            consoleLogFormatted.error(ERRORS.common.timeoutExceeded);
            consoleLogFormatted.error(condition.toString());
            return;
        }
        await waitUntilTimeout(100); // Poll every 100 milliseconds until timeout
    }
};
export const convertToSha1 = (text) => crypto.createHash("sha1").update(text).digest("hex").slice(0, 20);
export const generateHistoryId = ({ projectId, specName, describeTitles, testTitle, }) => {
    const titles = [...describeTitles, testTitle];
    const targetString = `[project=${projectId}]${specName}\x1e${titles.join("\x1e")}`;
    const targetStringSha = convertToSha1(targetString);
    const pathNameSha = convertToSha1(specName);
    return `${pathNameSha}-${targetStringSha}`;
};
export const joinHyphenCase = (...strings) => strings.join("-");
export const convertTopLevelKeysToSnakeCase = (object) => Object.entries(object).reduce((acc, curr) => {
    const snakeCaseKey = camelToSnakeCase(curr[0]);
    acc[snakeCaseKey] = curr[1];
    return acc;
}, {});
export const decryptKey = (encryptedKey) => {
    const iv = Buffer.from(INITIAL_VECTOR, "hex");
    const salt = Buffer.from(SALT, "hex");
    const secretKey = SECRET_KEY;
    try {
        const key = crypto.scryptSync(secretKey, Uint8Array.from(salt), 32);
        const keyObject = crypto.createSecretKey(Uint8Array.from(key));
        const ivView = Uint8Array.from(iv);
        const decipher = crypto.createDecipheriv("aes-256-cbc", keyObject, ivView);
        let decrypted = decipher.update(encryptedKey, "hex", "utf8");
        decrypted += decipher.final("utf8");
        return decrypted;
    }
    catch (error) {
        consoleLogFormatted.error("Failed to decrypt API key: " + error);
        throw new Error("Failed to initialize reporting");
    }
};
export const isErrorCodeExempted = (status = 422) => HONEYBADGER_IGNORED_STATUS_CODES.includes(status);
export const isErrorMessageExempted = (message) => {
    if (!message)
        return false;
    return HONEYBADGER_IGNORED_ERROR_MESSAGES.some(errorMessage => message.includes(errorMessage));
};
export const reportToHoneybadger = async ({ context, error, title, }) => {
    if (error instanceof AggregateError) {
        consoleLogFormatted.error(`AggregateError occurred with ${error.errors.length} individual errors:`);
        error.errors.forEach((err, index) => {
            consoleLogFormatted.error(`--- Error ${index + 1} ---`);
            consoleLogFormatted.error(`Message: ${err.message || "No message"}`);
            consoleLogFormatted.error(`Code: ${err.code || "No code"}`);
            consoleLogFormatted.error(`Name: ${err.name || "Unknown"}`);
            if (err.stack) {
                consoleLogFormatted.error(`Stack: ${err.stack.split("\n")[0]}`);
            }
            consoleLogFormatted.error("--------------------");
        });
        Honeybadger.setContext({
            ...context,
            errorDetails: {
                aggregateErrorCount: error.errors.length,
                individualErrors: error.errors.map(err => ({
                    message: err.message,
                    code: err.code,
                    name: err.name,
                })),
            },
            reporterVersion: getCurrentReporterVersion(),
        });
        return;
    }
    const response = error.response;
    const data = response === null || response === void 0 ? void 0 : response.data;
    Honeybadger.setContext({
        ...context,
        errorDetails: error,
        reporterVersion: getCurrentReporterVersion(),
    });
    if (!isErrorCodeExempted(response === null || response === void 0 ? void 0 : response.status) &&
        !isErrorMessageExempted(data === null || data === void 0 ? void 0 : data.error)) {
        await Honeybadger.notifyAsync((data === null || data === void 0 ? void 0 : data.error) ? data.error : error, title);
    }
};
export const getCurrentReporterVersion = () => {
    const packageJson = JSON.parse(readFileSync(join(__dirname, "./package.json"), "utf8"));
    return packageJson.version;
};
export const humanizeTime = (milliseconds) => {
    const time = {
        hour: Math.floor(milliseconds / (60 * 60 * 1000)),
        minute: Math.floor(milliseconds / (60 * 1000)) % 60,
        second: Math.floor(milliseconds / 1000) % 60,
        millisecond: Math.floor(milliseconds) % 1000,
    };
    return Object.entries(time)
        .filter(([_, value]) => value !== 0)
        .map(([unit, value]) => value + " " + (value !== 1 ? `${unit}s` : unit))
        .join(", ");
};
//# sourceMappingURL=common.js.map